import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowLeft, Search, Loader2, Plus, Trash2, Package, Folder, FolderPlus, X } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';

interface ExtrasType {
    id: string;
    category_id: string | null;
}

interface ExtrasCategory {
    id: string;
    name: string;
    type_count: number;
}

interface ExtrasPartner {
    id: string;
    name: string;
    types: ExtrasType[];
    categories: ExtrasCategory[];
}

const UNCATEGORIZED = 'none';

export const ExtrasPartnerTypesPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [partner, setPartner] = React.useState<ExtrasPartner | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [q, setQ] = React.useState('');
    const [showAddCategory, setShowAddCategory] = React.useState(false);
    const [newCategoryName, setNewCategoryName] = React.useState('');
    const [savingCategory, setSavingCategory] = React.useState(false);
    const [error, setError] = React.useState('');

    const load = React.useCallback(() => {
        if (!id) return;
        moduleApi.getExtrasPartner(id).then((p: any) => setPartner(p || null)).catch(() => setPartner(null)).finally(() => setLoading(false));
    }, [id]);

    React.useEffect(() => { load(); }, [load]);

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;
    if (!partner) {
        return (
            <div className="text-center">
                <p className="text-content-muted text-sm mb-4">Partenaire introuvable.</p>
                <Button variant="secondary" onClick={() => navigate('/extras-partenaires')}><ArrowLeft size={15} /> Retour</Button>
            </div>
        );
    }

    const handleAddCategory = async () => {
        if (!id || !newCategoryName.trim()) return;
        setSavingCategory(true);
        try {
            await moduleApi.createExtrasCategory(id, { name: newCategoryName.trim() });
            setNewCategoryName('');
            setShowAddCategory(false);
            load();
        } catch (e: any) {
            setError(e?.message || 'Impossible de créer cette catégorie.');
        } finally {
            setSavingCategory(false);
        }
    };

    const handleDeleteCategory = async (categoryId: string, name: string) => {
        if (!id) return;
        if (!confirm(`Supprimer la catégorie "${name}" ? Ses types seront conservés, simplement repassés "sans catégorie".`)) return;
        try {
            await moduleApi.deleteExtrasCategory(id, categoryId);
            load();
        } catch (e: any) {
            setError(e?.message || 'Impossible de supprimer cette catégorie.');
        }
    };

    const uncategorizedCount = partner.types.filter((t) => !t.category_id).length;
    const qLower = q.trim().toLowerCase();
    const cards: { id: string; name: string; type_count: number; deletable: boolean }[] = [
        ...partner.categories.map((cat) => ({ id: cat.id, name: cat.name, type_count: cat.type_count, deletable: true })),
        ...(uncategorizedCount > 0 ? [{ id: UNCATEGORIZED, name: 'Sans catégorie', type_count: uncategorizedCount, deletable: false }] : []),
    ].filter((cat) => !qLower || cat.name.toLowerCase().includes(qLower));

    return (
        <div className="space-y-6 max-w-3xl">
            <div>
                <button onClick={() => navigate(`/extras-partenaires/${id}`)} className="flex items-center gap-1.5 text-sm text-content-muted hover:text-content font-medium mb-2">
                    <ArrowLeft size={15} /> {partner.name}
                </button>
                <h1 className="text-xl font-bold text-content tracking-tight flex items-center gap-2"><Package size={18} /> Catalogue de types d'extras</h1>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[200px] max-w-sm">
                    <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-muted" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une catégorie..." className="pl-8" />
                </div>
                <Button variant="secondary" size="sm" onClick={() => setShowAddCategory((v) => !v)} className="shrink-0">
                    <FolderPlus size={14} /> Ajouter une catégorie
                </Button>
            </div>

            {showAddCategory && (
                <div className="flex items-center gap-1.5 max-w-sm">
                    <input value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)} placeholder="Nom de la catégorie" autoFocus
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAddCategory(); if (e.key === 'Escape') setShowAddCategory(false); }}
                        className="flex-1 min-w-0 bg-surface border border-border rounded-md px-2.5 py-2 text-sm text-content outline-none focus:border-brand-500" />
                    <Button size="sm" onClick={handleAddCategory} disabled={savingCategory || !newCategoryName.trim()}>
                        {savingCategory ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => setShowAddCategory(false)}><X size={13} /></Button>
                </div>
            )}

            {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}

            {cards.length === 0 ? (
                <p className="text-sm text-content-muted">Aucune catégorie pour l'instant.</p>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {cards.map((cat) => (
                        <motion.div
                            key={cat.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-surface rounded-2xl border border-border p-6 hover:border-white/20 transition-all cursor-pointer group relative overflow-hidden"
                            onClick={() => navigate(`/extras-partenaires/${id}/types/${cat.id}`)}
                        >
                            <div className="relative z-10">
                                <div className="flex justify-between items-start mb-6">
                                    <div className="w-12 h-12 bg-surface-soft rounded-xl flex items-center justify-center border border-border">
                                        <Folder className="text-content-muted" size={22} />
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-[10px] font-bold px-2 py-1 rounded uppercase tracking-widest bg-brand-500/10 text-brand-600 dark:text-brand-400 flex items-center gap-1">
                                            <Package size={11} /> {cat.type_count} type{cat.type_count > 1 ? 's' : ''}
                                        </span>
                                        {cat.deletable && (
                                            <button onClick={(e) => { e.stopPropagation(); handleDeleteCategory(cat.id, cat.name); }} title="Supprimer la catégorie"
                                                className="p-1.5 rounded-lg text-red-400 hover:bg-red-500/10">
                                                <Trash2 size={13} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <h3 className="text-xl font-bold text-content mb-1 group-hover:text-brand-600 dark:text-brand-400 transition-colors truncate">{cat.name}</h3>
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}
        </div>
    );
};
